"use client";

import { Bell, Loader, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
function InnerCircleForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="mx-auto max-w-xl rounded-lg border border-border bg-card p-8 text-center">
        <Bell className="mx-auto mb-4 size-8 text-primary" />
        <p className="font-default text-lg font-semibold text-card-foreground">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Submit Another
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="mx-auto w-full max-w-2xl text-left">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField control={control} name="first_name" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">First Name</FormLabel>
              <FormControl>
                <Input placeholder="Kira" className="h-11 border-0 border-b border-border bg-transparent text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="last_name" rules={{
        required: "Last name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Trainer" className="h-11 border-0 border-b border-border bg-transparent text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="mt-4 flex flex-col gap-3">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className="flex-1">
              <FormLabel className="text-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="kira@coachkira.fit" className="h-12 border-0 border-b border-border bg-transparent text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" rules={{}} render={({
      field
    }) => <FormItem className="mt-4">
            <FormLabel className="text-foreground">Phone Number</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(555) 000-0000" className="h-11 border-0 border-b border-border bg-transparent text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-0" {...field} />
            </FormControl>
            <FormDescription className="text-muted-foreground">Optional, for direct coaching updates</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="marketing_consent" rules={{
      required: "You must agree to receive coaching updates"
    }} render={({
      field
    }) => <FormItem className="mt-5">
            <div className="flex items-start gap-3">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-0.5 border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" />
              </FormControl>
              <div className="grid gap-1">
                <FormLabel className="text-sm font-normal text-foreground">
                  I want to receive fitness tips and program availability
                </FormLabel>
                <FormDescription className="text-muted-foreground">
                  Required to receive coaching communications
                </FormDescription>
              </div>
            </div>
            <FormMessage />
          </FormItem>} />

      <div className="mt-4 w-full">
        <Button type="submit" disabled={isSubmitting} className="h-12 w-full bg-primary px-8 text-base font-semibold text-primary-foreground hover:bg-primary/90">
          {isSubmitting ? <>
              <Loader className="mr-2 size-4 animate-spin" />
              Processing...
            </> : "GET STARTED"}
        </Button>
      </div>

      {submitError && <div className="mt-4 border border-destructive/40 bg-destructive/10 p-4">
          <p className="text-sm text-destructive-foreground">{submitError}</p>
        </div>}

      <p className="mt-5 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
        <Lock className="size-3.5" />
        Your data is secure. Opt-out at any time.
      </p>
    </Form>;
}
export default function StayMotivated() {
  return <section data-section-id="1978" id="stay-motivated" className="relative isolate overflow-hidden bg-background py-20 text-foreground sm:py-28">
      <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
        <span className="inline-block bg-primary/10 px-4 py-1.5 font-mono text-xs font-semibold uppercase tracking-widest text-primary">
          Start Your Transformation
        </span>

        <h2 className="mt-6 font-default text-5xl font-bold uppercase leading-none tracking-tighter text-foreground sm:text-7xl lg:text-8xl">
          Stay Motivated
        </h2>

        <p className="mx-auto mt-6 max-w-2xl text-base text-muted-foreground sm:text-lg">
          Ready to elevate your fitness? Join Coach Kira Fit today for personalized 
          coaching, sustainable health strategies, and the accountability you need 
          to reach your peak performance.
        </p>

        <div className="mt-10">
          <FormProvider formKey="motivation_signup_start_your_journey" formId="motivation_signup_start_your_journey" sectionName="stay-motivated" defaultValues={{
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
          marketing_consent: false
        }} validationRules={{
          first_name: {
            required: true
          },
          last_name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {},
          marketing_consent: {
            required: true
          }
        }} submitText="GET STARTED" successMessage="Welcome to the team! I'll be in touch shortly to discuss your goals." errorMessage="Submission failed. Please check your details.">
            <InnerCircleForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}